import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectContext } from 'Application/Context/ProjectContext';
import { Store } from "../../../Application/Store/Store";
import {
    COMPARATIVEVIEWERLEFT_LAYER,
    COMPARATIVEVIEWERRIGHT_LAYER,
    STRIPPING_VISIBLE
} from "../../../Application/Store/Actions/ViewportTypes";
import { toogleAnimationVisible } from '../../../Domain/Scene/Manager/SceneManager';
import { showComparativeElement } from '../../../Domain/Scene/Handler/AnimationHandler';
import { hasAnyToothBeenModified } from '../../../Domain/Mesh/Handler/MeshSelectorEvent';
import { computeAndDisplayTeethIntersection } from 'Domain/Scene/Handler/CollisionHandler';
import { pauseAnimation } from '../Player';
import { EditorManager } from "Domain/Scene/Manager/EditorManager";
import { targetMainCameraCenter } from 'Domain/Scene/Handler/CameraFacade';
import { resetViewportCamera } from 'Domain/Scene/Provider/ViewportCameraProvider';
import { delay } from 'Domain/Scene/Manager/InitializationOrchestrator';
import { sceneTransition } from 'Domain/Scene/Utils/SceneTransition';

export const useInformation = (props) => {
    const { passive, onEditMode, setOnEditMode, actions } = props;
    const project = useContext(ProjectContext);
    const dispatch = useDispatch();
    const { AnimationReducer, UserReducer, ViewportReducer } = useSelector(state => state);

    const metas = AnimationReducer.metas;
    const deniedServices = UserReducer.deniedServices || [];
    const viewerType = ViewportReducer.viewerType;
    const { versions, viewportMode, strippingVisibility, footerVisibility, selectedVersion, selectedComparativeVersion, comparativeViewerLeftLayer, mode } = ViewportReducer;

    const [currentValue, setCurrentValue] = useState(selectedVersion ?? 'single');
    const [currentLeftValue, setCurrentLeftValue] = useState(-2);
    const prevMode = useRef(viewportMode);
    const previousStrippingVisibility = useRef(strippingVisibility);
    const modeTransitionInProgress = useRef(false);

    const hasProjectRef = !!project?.ref?.sk;
    const hasCaseDataId = !!metas?.caseData?.caseID;
    const logoUrl = hasProjectRef
        ? `${process.env.REACT_APP_U1_VIEWERSVC_API}project/${project.ref.sk}/logo?dark=true`
        : hasCaseDataId
            ? `${process.env.REACT_APP_U1_VIEWERSVC_API}project/${metas.caseData.caseID}/logo?dark=true`
            : `${process.env.REACT_APP_U1_VIEWERSVC_API}company/logo?dark=true`;

    const getAnonymous = (name = '') => name.split(' ').map(n => n[0] + 'xxxx').join('');

    const isConnected = UserReducer.connected;
    const hasToothModifications = hasAnyToothBeenModified();
    const hasChanges = onEditMode && hasToothModifications;
    const isComparative = viewportMode === 'comparative';

    useEffect(() => {
        if (prevMode.current !== viewportMode) {
            setCurrentValue(isComparative ? -3 : selectedVersion);
            setCurrentLeftValue(comparativeViewerLeftLayer === 'maloc' ? -2 : selectedComparativeVersion);
        } else if (!isComparative) {
            setCurrentValue(selectedVersion);
        }
        prevMode.current = viewportMode;
    }, [viewportMode, isComparative, selectedVersion, comparativeViewerLeftLayer, selectedComparativeVersion]);

    const handleLayerChange = useCallback(async (e, side = 'right') => {
        const selectedValue = e.target.value;
        const numericVersionId = parseInt(selectedValue, 10);

        if (side === 'right') {
            setCurrentValue(selectedValue);
            if (numericVersionId === -3) {
                Store.dispatch({ type: COMPARATIVEVIEWERRIGHT_LAYER, payload: 'setup' });
                return;
            }
            if (selectedValue || selectedValue === 0) {
                Store.dispatch({ type: STRIPPING_VISIBLE, payload: false });
                Store.dispatch({ type: COMPARATIVEVIEWERRIGHT_LAYER, payload: 'version' });
                await EditorManager.selectVersion(selectedValue);
            }
            return;
        }

        if (numericVersionId === -2) {
            Store.dispatch({ type: COMPARATIVEVIEWERLEFT_LAYER, payload: 'maloc' });
            setCurrentLeftValue(-2);
            showComparativeElement(false);
        } else if (selectedValue || selectedValue === 0) {
            Store.dispatch({ type: COMPARATIVEVIEWERLEFT_LAYER, payload: 'version' });
            setCurrentLeftValue(numericVersionId);
            await EditorManager.selectComparativeVersion(selectedValue);
        }
    }, []);

    const handleVersionChange = useCallback(async (e) => {
        await sceneTransition(async () => {
            await handleLayerChange(e, 'right');
        });
    }, [handleLayerChange]);
    const handleComparativeVersionChange = useCallback(async (e) => {
        await sceneTransition(async () => {
            await handleLayerChange(e, 'left');
        });
    }, [handleLayerChange]);

    const applyViewportActions = useCallback((selected) => {
        if (selected === 'comparative') {
            Store.dispatch({ type: COMPARATIVEVIEWERRIGHT_LAYER, payload: 'setup' });
            Store.dispatch({ type: COMPARATIVEVIEWERLEFT_LAYER, payload: 'maloc' });
            Store.dispatch({ type: STRIPPING_VISIBLE, payload: false });
            Store.dispatch(toogleAnimationVisible('none'));
            return;
        }

        Store.dispatch({ type: STRIPPING_VISIBLE, payload: previousStrippingVisibility.current });
        Store.dispatch({ type: COMPARATIVEVIEWERRIGHT_LAYER, payload: 'version' });

        if (Store.getState().ViewportReducer.selectedVersion === -1 && !onEditMode) {
            Store.dispatch(toogleAnimationVisible('flex'));
        }
    }, [onEditMode]);

    const handleViewportChange = useCallback(async (e) => {
        const selected = e.target.value;

        await sceneTransition(async () => {
            targetMainCameraCenter();
            resetViewportCamera(selected);

            dispatch({ type: 'VIEWPORT_MODE', payload: selected });
            previousStrippingVisibility.current = strippingVisibility;

            applyViewportActions(selected);

            await delay(100);

            if (onEditMode) await computeAndDisplayTeethIntersection();
        });
    }, [dispatch, onEditMode, strippingVisibility, applyViewportActions]);

    const handleEnterEdition = useCallback(async () => {
        if (modeTransitionInProgress.current) return;
        modeTransitionInProgress.current = true;
        pauseAnimation();
        try {
            await sceneTransition(async () => {
                EditorManager.enterEditMode(setOnEditMode);
            });
        } finally {
            modeTransitionInProgress.current = false;
        }
    }, [setOnEditMode]);

    const handleSaveAndExit = useCallback(async () => {
        if (!hasAnyToothBeenModified()) return;
        await sceneTransition(async () => {
            await EditorManager.exitEditMode(setOnEditMode, true);
        });
    }, [setOnEditMode]);

    return {
        passive,
        onEditMode,
        actions,
        deniedServices,
        viewerType,
        versions,
        viewportMode,
        footerVisibility,
        selectedVersion,
        mode,
        currentValue,
        setCurrentValue,
        currentLeftValue,
        logoUrl,
        getAnonymous,
        metas,
        UserReducer,
        isConnected,
        hasChanges,
        isComparative,
        handleLayerChange,
        handleVersionChange,
        handleComparativeVersionChange,
        handleViewportChange,
        handleEnterEdition,
        handleSaveAndExit,
        AnimationReducer,
        setOnEditMode,
    };
};
